// src/components/HistoryFilters/HistoryFilters.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import './HistoryFilters.css';
import translationEn from '../../i18n/supported_languages/en/translation.json';

// Material-UI Imports (no changes)
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Box,
    InputAdornment,
    Button,
    Typography,
    IconButton,
    Tooltip,
    Autocomplete,
    Slider,
    ListSubheader,
    Link,  
} from '@mui/material';

// Icon Imports (no changes)
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';

// We need the initial state here for comparison
const INITIAL_FILTERS = {
    contentTypes: ['text', 'image', 'audio', 'video'],
    scoreTypes: [],
    likelihoods: ['Yes', 'No'], // Added likelihoods to initial filters
    filenameSearch: '',
    startDate: '',
    endDate: '',
    score_vetra_score: [0, 100],
    score_ai_detection: [0, 100],
    score_fake_news_detection: [0, 100],
};


// Robust array comparison helper function
const areArraysEqual = (arr1, arr2) => {
    // If either input is not an array, they can't be equal.
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
};


const HistoryFilters = ({ filters, onFilterChange, onResetFilters, onClose, filenameOptions, tasksConfig, masterTaskOrder, dynamicInitialFilters, availableFilterTasks }) => {
    const { t } = useTranslation();

    // Define translated constants INSIDE the component
    const CONTENT_TYPE_OPTIONS = [
        { key: 'text', name: t('historyFilters.contentType.options.text') },
        { key: 'image', name: t('historyFilters.contentType.options.image') },
        { key: 'audio', name: t('historyFilters.contentType.options.audio') },
        { key: 'video', name: t('historyFilters.contentType.options.video') },
    ];

    const LIKELIHOOD_OPTIONS = [
        { key: 'Yes', name: t('historyFilters.likelihood.options.yes') },
        { key: 'No', name: t('historyFilters.likelihood.options.no') },
    ];

    // Maps over availableFilterTasks instead of masterTaskOrder to hide unused features
    const ANALYSIS_TYPE_OPTIONS = (availableFilterTasks || []).map(taskKey => ({
        key: taskKey,
        name: t(`verificationPage.analysisTasks.${taskKey}`, { 
            defaultValue: tasksConfig?.[taskKey]?.display_name || taskKey 
        })
    }));

    const CATEGORY_OPTIONS = Object.keys(translationEn.contentMetadata.inclusion).map(key => ({
        key: key,
        name: t(`contentMetadata.inclusion.${key}`)
    })).sort((a, b) => a.name.localeCompare(b.name));

    const SAFETY_FLAG_OPTIONS = Object.keys(translationEn.contentMetadata.exclusion).map(key => ({
        key: key,
        name: t(`contentMetadata.exclusion.${key}`)
    })).sort((a, b) => a.name.localeCompare(b.name));

    // --- Content Category ---
    const isCategoryActive = filters.contentCategories.length < CATEGORY_OPTIONS.length;
    // Logic: If all selected, show "All Categories". Else show "Category (X)" or list.
    // Actually, following your design: "Content Category (X)"
    const categoryLabel = t('historyFilters.contentCategory.labelWithCount', { 
        count: filters.contentCategories.length 
    });
    // Reset Handler: Set to ALL keys
    const handleResetCategories = () => onFilterChange('contentCategories', CATEGORY_OPTIONS.map(o => o.key));

    // --- Safety Flags ---
    // Active if length > 0 (since default is empty)
    const isSafetyActive = filters.safetyFlags.length > 0;
    const safetyLabel = t('historyFilters.safetyFlags.labelWithCount', { 
        count: filters.safetyFlags.length 
    });
    // Reset Handler: Set to EMPTY (Show All)
    const handleResetSafety = () => onFilterChange('safetyFlags', []);

    // Handlers are unchanged
    const handleInputChange = (event) => onFilterChange(event.target.name, event.target.value);
    const handleMultiSelectChange = (event) => {
        const { name, value } = event.target;
        onFilterChange(name, typeof value === 'string' ? value.split(',') : value);
    };

    const handleSliderChange = (name, newValue) => {
        onFilterChange(name, newValue);
    };

    // Helper function for individual filter resets
    const handleSingleFilterReset = (filterName) => {
        if (filterName === 'scoreTypes') {
            onFilterChange('scoreTypes', dynamicInitialFilters.scoreTypes);
            // Reset ALL sliders dynamically
            masterTaskOrder.forEach(taskKey => {
                onFilterChange(`score_${taskKey}`, [0, 100]);
            });
        } else {
            onFilterChange(filterName, dynamicInitialFilters[filterName]);
        }
    };

    const isMultiSelectActive = (filterName) => {
        return !areArraysEqual(filters[filterName], dynamicInitialFilters[filterName]);
    }

    // Get today's date for setting the max attribute
    const todayString = new Date().toISOString().split('T')[0];

    // --- CREATE A MANUAL TOGGLE HANDLER ---
    // This function will manually add or remove a score type from the array,
    // giving us full control and bypassing the Select's buggy onChange.
    const handleScoreTypeToggle = (scoreTypeKey) => {
        const currentScoreTypes = filters.scoreTypes;
        const newScoreTypes = currentScoreTypes.includes(scoreTypeKey)
            ? currentScoreTypes.filter(type => type !== scoreTypeKey) // If it exists, remove it
            : [...currentScoreTypes, scoreTypeKey];                 // If it doesn't exist, add it

        // Call the parent's handler with the manually constructed array
        onFilterChange('scoreTypes', newScoreTypes);
    };

    const handleSelectAll = (filterName, allOptions) => {
        onFilterChange(filterName, allOptions.map(opt => opt.key));
    };

    const handleClearAll = (filterName) => {
        onFilterChange(filterName, []);
    };

    const areScoreFiltersActive = () => {
        // Check if the main checkboxes have changed
        if (!areArraysEqual(filters.scoreTypes, dynamicInitialFilters.scoreTypes)) return true;
        
        // --- Safely check dynamic sliders ---
        for (const taskKey of masterTaskOrder) {
            // Apply the fallback here before comparing, so undefined === [0, 100]
            const currentSlider = filters[`score_${taskKey}`] || [0, 100];
            if (!areArraysEqual(currentSlider, [0, 100])) return true;
        }
        
        return false;
    };

    return (
        <div className="filter-drawer-container">
            <header className="filter-drawer-header">
                <Typography variant="h6" component="h3">{t('historyFilters.title')}</Typography>
                <IconButton onClick={onClose} aria-label={t('historyFilters.closeLabel')}><CloseIcon /></IconButton>
            </header>

            <div className="filter-drawer-body">

                {/* --- Filename Filter --- */}
                <div className={`filter-item-wrapper ${filters.filenameSearch !== INITIAL_FILTERS.filenameSearch ? 'active-filter' : ''}`}>
                    <Autocomplete
                        freeSolo // Allows the user to type text not in the list
                        fullWidth
                        options={filenameOptions}
                        value={filters.filenameSearch}
                        // This handles both selecting an option and typing custom text
                        onChange={(event, newValue) => {
                            onFilterChange('filenameSearch', newValue);
                        }}
                        // This specifically handles live typing
                        onInputChange={(event, newInputValue) => {
                            onFilterChange('filenameSearch', newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('historyFilters.filename.label')}
                                variant="outlined"
                                className="filter-text-field" // Reuse existing class for consistency
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                    {filters.filenameSearch !== INITIAL_FILTERS.filenameSearch && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow><IconButton className="single-reset-btn" onClick={() => onFilterChange('filenameSearch', INITIAL_FILTERS.filenameSearch)} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                </div>

                {/* --- Content Type Filter --- */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isMultiSelectActive('contentTypes') ? 'active-filter' : ''}`}>
                        <InputLabel>{t('historyFilters.contentType.label')}</InputLabel>
                        <Select multiple name="contentTypes" value={filters.contentTypes} onChange={handleMultiSelectChange} input={<OutlinedInput label={t('historyFilters.contentType.label')} />} 
                            renderValue={(selected) => {
                                // If all options are selected, show "All Content Types"
                                if (selected.length === CONTENT_TYPE_OPTIONS.length) {
                                    return t('historyFilters.contentType.options.allContentTypes'); 
                                }
                                // Otherwise, list them comma-separated
                                return selected.map(s => t(`historyFilters.contentType.options.${s}`)).join(', ');
                            }}
                            MenuProps={{ className: 'history-multi-select-menu' }}>
                            {CONTENT_TYPE_OPTIONS.map((type) => (<MenuItem key={type.key} value={type.key}><Checkbox checked={filters.contentTypes.indexOf(type.key) > -1} /><ListItemText primary={type.name} /></MenuItem>))}
                        </Select>
                    </FormControl>
                    {isMultiSelectActive('contentTypes') && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow><IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('contentTypes')} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                </div>

                {/* --- Analysis Score Filter --- */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${areScoreFiltersActive() ? 'active-filter' : ''}`}>
                        <InputLabel>{t('historyFilters.analysisScore.label')}</InputLabel>
                        <Select
                            multiple
                            name="scoreTypes"
                            value={filters.scoreTypes || []}
                            input={<OutlinedInput label={t('historyFilters.analysisScore.label')} />}
                            renderValue={(selected) => {
                                let displayText = '';

                                // 1. If ALL or NONE are selected, show the default "All Analysis Scores" text
                                if (!ANALYSIS_TYPE_OPTIONS || selected.length === ANALYSIS_TYPE_OPTIONS.length || selected.length === 0) {
                                    displayText = t('historyFilters.analysisScore.options.allAnalysisScores');
                                } 
                                // 2. If MORE THAN 2 are selected, show the summary counter
                                else if (selected.length > 2) {
                                    const count = selected.length;
                                    const i18nKey = 'selector.analysisTasks.selectedSummary';
                                    const translated = t(i18nKey, { count });
                                    // Fallback to English if translation is missing
                                    displayText = translated !== i18nKey ? translated : `${count} Tasks Selected`;
                                } 
                                // 3. If 1 or 2 are selected, sort them by YAML order and join with comma
                                else {
                                    displayText = [...selected]
                                        .sort((a, b) => {
                                            const indexA = ANALYSIS_TYPE_OPTIONS.findIndex(t => t.key === a);
                                            const indexB = ANALYSIS_TYPE_OPTIONS.findIndex(t => t.key === b);
                                            return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                                        })
                                        .map(val => {
                                            const opt = ANALYSIS_TYPE_OPTIONS.find(o => o.key === val);
                                            return opt ? opt.name : val;
                                        })
                                        .join(', ');
                                }

                                // 4. Wrap in Typography to ensure it respects boundaries and adds "..." if still too long
                                return (
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '1rem',
                                            lineHeight: '1.5'
                                        }}
                                    >
                                        {displayText}
                                    </Typography>
                                );
                            }}
                            MenuProps={{ 
                                className: 'history-multi-select-menu',
                                disableAutoFocusItem: true,
                                PaperProps: {
                                    style: {
                                        maxHeight: 400, 
                                        overflowY: 'auto'
                                    },
                                },
                            }}
                        >
                            {(ANALYSIS_TYPE_OPTIONS || []).map((option) => {
                                const isMeta = tasksConfig?.[option.key]?.is_meta_task;
                                const tooltipDesc = t(`verificationPage.scoreDefinitions.${option.key}`, { 
                                    defaultValue: tasksConfig?.[option.key]?.description || '' 
                                });
                                
                                // --- Check if this specific task is checked ---
                                const isActive = (filters.scoreTypes || []).indexOf(option.key) > -1;

                                return (
                                    <MenuItem key={option.key} value={option.key} className="composite-menu-item">
                                        <div className="composite-menu-item-content">
                                            <Tooltip title={tooltipDesc} arrow placement="right" PopperProps={{ className: 'global-dark-tooltip' }}>
                                                <div className="checkbox-row" onClick={() => handleScoreTypeToggle(option.key)}>
                                                    <Checkbox checked={isActive} />
                                                    
                                                    {isMeta && (
                                                        <img 
                                                            src="/static/vetraai_logo_dark_blue.webp" 
                                                            alt="Meta Score" 
                                                            style={{ width: '16px', height: '16px', marginRight: '8px', objectFit: 'contain' }} 
                                                        />
                                                    )}

                                                    <ListItemText 
                                                        primary={option.name} 
                                                        primaryTypographyProps={{
                                                            style: { 
                                                                color: isMeta ? 'var(--secondary-color)' : 'inherit',
                                                                fontWeight: isMeta ? 600 : 400
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </Tooltip>

                                            {/* --- Always render, but disable and gray out if not active --- */}
                                            <Box
                                                className="slider-in-menu"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                sx={{ 
                                                    opacity: isActive ? 1 : 0.4, 
                                                    pointerEvents: isActive ? 'auto' : 'none',
                                                    transition: 'opacity 0.2s ease'
                                                }}
                                            >
                                                <Slider
                                                    disabled={!isActive} // Makes the thumb grey and un-draggable
                                                    value={filters[`score_${option.key}`] || [0, 100]}
                                                    onChange={(e, val) => handleSliderChange(`score_${option.key}`, val)}
                                                    valueLabelDisplay="auto"
                                                    min={0}
                                                    max={100}
                                                    marks={[{ value: 0, label: '0%' }, { value: 100, label: '100%' }]}
                                                />
                                            </Box>
                                        </div>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    {areScoreFiltersActive() && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('scoreTypes')} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* --- Likelihood Filter --- */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isMultiSelectActive('likelihoods') ? 'active-filter' : ''}`}>
                        <InputLabel>{t('historyFilters.likelihood.label')}</InputLabel>
                        <Select
                            multiple
                            name="likelihoods"
                            value={filters.likelihoods}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={t('historyFilters.likelihood.label')} />}
                            renderValue={(selected) => selected.map(s => t(`historyFilters.likelihood.options.${s.toLowerCase()}`)).join(', ')}
                            MenuProps={{ className: 'history-multi-select-menu' }}
                        >
                            {LIKELIHOOD_OPTIONS.map((option) => (
                                <MenuItem key={option.key} value={option.key}>
                                    <Checkbox checked={filters.likelihoods.indexOf(option.key) > -1} />
                                    <ListItemText primary={option.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isMultiSelectActive('likelihoods') && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('likelihoods')} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* --- Safety Flags Filter --- */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isSafetyActive ? 'active-filter' : ''}`}>
                        <InputLabel shrink>{safetyLabel}</InputLabel>
                        <Select
                            multiple
                            displayEmpty
                            name="safetyFlags"
                            value={filters.safetyFlags}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={safetyLabel} />}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return (
                                        <span style={{ fontStyle: 'normal' }}>
                                            {t('historyFilters.safetyFlags.noFlagsSelected')}
                                        </span>
                                    );
                                }
                                return selected.map(s => t(`contentMetadata.exclusion.${s}`)).join(', ');
                            }}
                            MenuProps={{ 
                                className: 'history-multi-select-menu',
                                autoFocus: false,
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                        >
                            <ListSubheader>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="overline">{t('historyFilters.actions')}</Typography>
                                    <Box>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleSelectAll('safetyFlags', SAFETY_FLAG_OPTIONS)}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('historyFilters.selectAll')}
                                        </Link>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleClearAll('safetyFlags')}
                                        >
                                            {t('historyFilters.clearAll')}
                                        </Link>
                                    </Box>
                                </Box>
                            </ListSubheader>
                            {SAFETY_FLAG_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={filters.safetyFlags.indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isSafetyActive && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={handleResetSafety} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* --- Content Category Filter --- */}
                <div className="filter-item-wrapper">
                    <FormControl className={`filter-form-control ${isCategoryActive ? 'active-filter' : ''}`}>
                        <InputLabel>{categoryLabel}</InputLabel>
                        <Select
                            multiple
                            name="contentCategories"
                            value={filters.contentCategories}
                            onChange={handleMultiSelectChange}
                            input={<OutlinedInput label={categoryLabel} />}
                            renderValue={(selected) => {
                                // If all categories are selected, show summary label
                                if (selected.length === CATEGORY_OPTIONS.length) {
                                    return t('historyFilters.contentCategory.allContentCategories');
                                }
                                
                                // Otherwise, list the selected names
                                return selected
                                    .map(s => t(`contentMetadata.inclusion.${s}`))
                                    .join(', ');
                            }}
                            MenuProps={{ 
                                className: 'history-multi-select-menu',
                                autoFocus: false,
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        // width: 250,
                                    },
                                },
                            }}
                        >
                            <ListSubheader>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="overline">{t('historyFilters.actions')}</Typography>
                                    <Box>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleSelectAll('contentCategories', CATEGORY_OPTIONS)}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('historyFilters.selectAll')}
                                        </Link>
                                        <Link 
                                            component="button" 
                                            variant="body2"
                                            onClick={() => handleClearAll('contentCategories')}
                                        >
                                            {t('historyFilters.clearAll')}
                                        </Link>
                                    </Box>
                                </Box>
                            </ListSubheader>
                            {CATEGORY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                    <Checkbox checked={filters.contentCategories.indexOf(opt.key) > -1} />
                                    <ListItemText primary={opt.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isCategoryActive && (
                        <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow>
                            <IconButton className="single-reset-btn" onClick={handleResetCategories} size="small">
                                <ReplayIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                {/* --- Date Range Filter --- */}
                <Box className="date-range-filter">
                    <div className={`filter-item-wrapper ${filters.startDate !== INITIAL_FILTERS.startDate ? 'active-filter' : ''}`}>
                        <TextField
                            name="startDate"
                            label={t('historyFilters.dateRange.from')}
                            type="date"
                            value={filters.startDate}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            className="filter-text-field"
                            inputProps={{
                                // The "To" date is the maximum allowed "From" date.
                                // If no "To" is set, the max is today.
                                max: filters.endDate || todayString
                            }}
                        />
                        {filters.startDate !== INITIAL_FILTERS.startDate && (
                            <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow><IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('startDate')} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                    </div>
                    <div className={`filter-item-wrapper ${filters.endDate !== INITIAL_FILTERS.endDate ? 'active-filter' : ''}`}>
                        <TextField
                            name="endDate"
                            label={t('historyFilters.dateRange.to')}
                            type="date"
                            value={filters.endDate}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            className="filter-text-field"
                            inputProps={{
                                // The "From" date is the minimum allowed "To" date.
                                min: filters.startDate,
                                max: todayString
                            }}
                        />
                        {filters.endDate !== INITIAL_FILTERS.endDate && (
                            <Tooltip title={t('historyFilters.resetFilterTooltip')} arrow><IconButton className="single-reset-btn" onClick={() => handleSingleFilterReset('endDate')} size="small"><ReplayIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                    </div>
                </Box>
            </div>

            <div className="reset-button-container">
                <Button variant="text" onClick={onResetFilters} className="reset-filters-btn">{t('historyFilters.resetAllButton')}</Button>
            </div>
        </div>
    );
};

export default HistoryFilters;